/** @format */

import React, { useState } from "react";
import { AiOutlineCloudUpload, AiOutlineCheckCircle } from "react-icons/ai";
import { BiErrorCircle } from "react-icons/bi";
import { ImSpinner2 } from "react-icons/im";
import { MdCancel } from "react-icons/md";
import {
  Button,
  ProgressBar,
  Alert,
  Form,
  Card,
  Spinner,
} from "react-bootstrap";

const ResumeUploader = () => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // File validation
  const validateFile = (file) => {
    const validTypes = ["application/pdf", "text/plain"];
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (!validTypes.includes(file.type))
      return "Only PDF or text files are allowed";
    if (file.size > maxSize) return "File size should not exceed 5MB";
    return "";
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (selectedFile) {
      const validationError = validateFile(selectedFile);
      if (validationError) {
        setError(validationError);
        setFile(null);
      } else {
        setError("");
        setFile(selectedFile);
        setSuccess(false); // Reset success when a new file is selected
      }
    }
  };

  // Handle file upload (without Firebase functionality)
  const handleUpload = () => {
    if (!file) return;

    // Simulate upload progress
    setIsUploading(true);
    const uploadSimulation = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(uploadSimulation);
          setIsUploading(false);
          setSuccess(true);
          setFile(null);
          return 100;
        }
        return prev + 10; // Increment progress
      });
    }, 300);
  };

  // Handle cancel upload
  const handleCancelUpload = () => {
    setIsUploading(false); // Stop spinner
    setProgress(0); // Reset progress
    setError("Upload cancelled");
  };

  return (
    <div
      className="mb-4 bg-light bg-opacity-50 d-flex justify-content-center align-items-center"
      style={{
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Card className="p-4 bg-transparent">
        <h2 className="text-center mb-4">Upload your Resume</h2>

        {/* File Input */}
        <Form.Group className="mb-4">
          <Form.Label className="w-100">
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept=".pdf, .txt"
              className="d-none"
            />
            <div className="d-flex flex-column align-items-center justify-content-center border border-dashed border-secondary p-4 rounded">
              <AiOutlineCloudUpload className="text-primary mb-2" size={48} />
              <p className="text-muted">
                Drag and drop, or click to select a file
              </p>
            </div>
          </Form.Label>
        </Form.Group>

        {/* Error Message */}
        {error && (
          <Alert variant="danger" className="d-flex align-items-center">
            <BiErrorCircle className="me-2" />
            {error}
          </Alert>
        )}

        {/* File Preview */}
        {file && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <strong>Selected file:</strong> {file.name}
            </div>
            <Button variant="danger" onClick={() => setFile(null)}>
              <MdCancel size={24} />
            </Button>
          </div>
        )}

        {/* Upload Button */}
        <div className="d-flex justify-content-center">
          <Button
            onClick={handleUpload}
            disabled={isUploading || !file}
            className={file && !isUploading ? "btn-primary" : "btn-secondary"}
          >
            {isUploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>

          {/* Cancel Upload Button */}
          {isUploading && (
            <Button variant="outline-danger" onClick={handleCancelUpload}>
              Cancel Upload
            </Button>
          )}
        </div>

        {/* Progress Bar */}
        {progress > 0 && (
          <div className="mt-4">
            <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
          </div>
        )}

        {/* Success Message */}
        {success && (
          <Alert variant="success" className="d-flex align-items-center mt-4">
            <AiOutlineCheckCircle className="me-2" />
            File uploaded successfully!
          </Alert>
        )}
      </Card>
    </div>
  );
};

export default ResumeUploader;
